import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    // ── [BACK-POSITION FIX] 상품 클릭 시 기준 카드 저장 + 뒤로가기 복원 ──
    this.addEventListener('click', this.#handleProductLinkClick, true);
    this.#restoreScrollPosition();
    // ────────────────────────────────────────────────────────────────
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    // ── [BACK-POSITION FIX] 클릭 리스너 해제 ──
    this.removeEventListener('click', this.#handleProductLinkClick, true);
    // ──────────────────────────────────────────
  }

  // ═══════════════════════════════════════════════════════════════════
  // [BACK-POSITION FIX] 시작
  // 문제: 스크롤 시 pushState로 URL이 ?page=N으로 바뀌기 때문에,
  //       뒤로가기 시 해당 페이지만 렌더링된 상태에서 브라우저가
  //       예전 절대 스크롤 좌표로 복원을 시도해 위치가 어긋남.
  // 해결: 클릭한 상품 카드를 "기준점"으로 저장하고, 돌아오면 그 카드를
  //       찾아 클릭 당시와 같은 화면 위치에 오도록 스크롤을 정렬한다.
  //       카드가 현재 렌더링에 없으면, 이 컴포넌트의 자체 기능인
  //       #renderPreviousPage()로 앞 페이지를 붙여가며 찾는다.
  // ═══════════════════════════════════════════════════════════════════

  /** 저장 키: 섹션 + 경로 기준 (page 파라미터는 제외) */
  #positionStorageKey() {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.sort();
    const qs = params.toString();
    return `plpos:${this.sectionId}:${window.location.pathname}${qs ? '?' + qs : ''}`;
  }

  /** 상품 링크 클릭 시, 클릭한 카드와 화면상 위치를 저장 */
  #handleProductLinkClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const link = target?.closest?.('a[href*="/products/"]');
    if (!link || !this.contains(link)) return;

    const card = link.closest('[ref="cards[]"]') || link;
    const href = (link.getAttribute('href') || '').split('?')[0];
    if (!href) return;

    try {
      sessionStorage.setItem(
        this.#positionStorageKey(),
        JSON.stringify({
          href,
          offset: card.getBoundingClientRect().top, // 클릭 당시 카드의 뷰포트 기준 위치
          t: Date.now(),
        })
      );
    } catch {
      // sessionStorage 사용 불가 환경이면 조용히 통과
    }
  };

  /** 저장된 href로 카드 찾기 */
  #findSavedCard(href) {
    const links = this.querySelectorAll('a[href*="/products/"]');
    for (const a of links) {
      const h = (a.getAttribute('href') || '').split('?')[0];
      if (h === href) return a.closest('[ref="cards[]"]') || a;
    }
    return null;
  }

  /** 뒤로가기로 돌아온 경우, 클릭했던 카드 기준으로 스크롤 복원 */
  async #restoreScrollPosition() {
    let saved = null;
    try {
      const raw = sessionStorage.getItem(this.#positionStorageKey());
      if (raw) saved = JSON.parse(raw);
    } catch {
      return;
    }
    if (!saved || !saved.href) return;

    // 30분 지난 기록은 무시
    if (Date.now() - saved.t > 30 * 60 * 1000) return;

    // 뒤로/앞으로 이동이거나, 방금(3분 내) 떠난 흔적이 있을 때만 복원
    const navEntry = performance.getEntriesByType?.('navigation')?.[0];
    const isBackForward = navEntry
      ? navEntry.type === 'back_forward'
      : performance.navigation?.type === 2;
    const recentlyLeft = Date.now() - saved.t < 3 * 60 * 1000;
    if (!isBackForward && !recentlyLeft) return;

    // 1회용: 같은 기록으로 반복 복원되지 않도록 즉시 제거
    try {
      sessionStorage.removeItem(this.#positionStorageKey());
    } catch {}

    // 브라우저의 자체 스크롤 복원(어긋남의 주범)을 비활성화
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    // 뷰 트랜지션이 진행 중이면 끝날 때까지 대기
    if (viewTransition.current) await viewTransition.current;

    let card = this.#findSavedCard(saved.href);

    // 현재 렌더링에 카드가 없으면, 테마 자체 기능으로 앞 페이지를 붙여가며 찾는다
    let attempts = 0;
    while (!card && attempts < 12) {
      attempts++;
      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) break;
      await this.#renderPreviousPage();
      card = this.#findSavedCard(saved.href);
    }

    if (!card) return; // 못 찾으면 아무것도 하지 않음 (기존 동작 유지)

    const align = () => {
      const targetCard = this.#findSavedCard(saved.href);
      if (!targetCard) return;
      const top =
        targetCard.getBoundingClientRect().top + window.scrollY - (saved.offset || 0);
      window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
    };

    align();
    // 이미지 로딩 등으로 레이아웃이 밀리는 경우 재보정
    setTimeout(align, 100);
    setTimeout(align, 300);
    setTimeout(align, 700);
  }

  // ═══════════════════════════════════════════════════════════════════
  // [BACK-POSITION FIX] 끝
  // ═══════════════════════════════════════════════════════════════════

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}